import React, { useCallback, useEffect, useState } from "react";
import { Layout, Spin } from "antd";
import PageTitle from "../../PageTitle";
import LeaveNavigation from "./navigation/LeaveNavigation";
import useAxios from "../../../utilities/useAxios";

const { Content } = Layout;

const formatRemainingDays = (value) => {
	if (value === null || value === undefined || value === "") {
		return "—";
	}
	const number = Number(value);
	if (!Number.isFinite(number)) {
		return "—";
	}
	const rounded = Math.round(number * 10) / 10;
	const text = Number.isInteger(rounded)
		? String(rounded)
		: rounded.toFixed(1);
	return `${text}일`;
};

const StatusIcon = ({ name }) => {
	const common = {
		viewBox: "0 0 24 24",
		"aria-hidden": true,
		focusable: "false",
	};
	switch (name) {
		case "check":
			return (
				<svg {...common}>
					<path d="M6 12.5 10 16.5 18 7.5" />
				</svg>
			);
		case "clock":
		case "late":
			return (
				<svg {...common}>
					<circle cx="12" cy="12" r="7.5" />
					<path d="M12 8.2V12l2.8 1.8" />
				</svg>
			);
		case "alert":
			return (
				<svg {...common}>
					<path d="M12 7.5v6" />
					<circle cx="12" cy="16.6" r="0.9" fill="currentColor" />
				</svg>
			);
		case "sunrise":
			return (
				<svg {...common}>
					<path d="M5 17h14" />
					<path d="M8 17a4 4 0 0 1 8 0" />
					<path d="M12 5v3" />
					<path d="M7.8 8.2 9.2 9.6M16.2 8.2 14.8 9.6" />
				</svg>
			);
		case "sun":
			return (
				<svg {...common}>
					<circle cx="12" cy="12" r="3.1" />
					<path d="M12 4.5v2.1M12 17.4v2.1M4.5 12h2.1M17.4 12h2.1M6.4 6.4l1.5 1.5M16.1 16.1l1.5 1.5M17.6 6.4l-1.5 1.5M7.9 16.1l-1.5 1.5" />
				</svg>
			);
		case "wave":
			return (
				<svg {...common}>
					<path d="M3 13c1.8 0 1.8-2.4 3.6-2.4S8.4 13 10.2 13s1.8-2.4 3.6-2.4S15.6 13 17.4 13 19.2 10.6 21 10.6" />
					<path d="M3 17.2c1.8 0 1.8-2.4 3.6-2.4s1.8 2.4 3.6 2.4 1.8-2.4 3.6-2.4 1.8 2.4 3.6 2.4 1.8-2.4 3.6-2.4" />
				</svg>
			);
		case "heart":
			return (
				<svg {...common}>
					<path d="M12 18.2s-5.4-3.2-5.4-7.2a3.1 3.1 0 0 1 5.4-.9 3.1 3.1 0 0 1 5.4.9c0 4-5.4 7.2-5.4 7.2z" />
				</svg>
			);
		case "plus":
			return (
				<svg {...common}>
					<path d="M12 6.5v11M6.5 12h11" />
				</svg>
			);
		case "trip":
			return (
				<svg {...common}>
					<rect x="4.5" y="8" width="15" height="10" rx="1.6" />
					<path d="M9 8V6.4h6V8" />
				</svg>
			);
		case "rest":
			return (
				<svg {...common}>
					<path d="M4 17v1.5M20 17v1.5" />
					<path d="M3.5 13.5h17V17h-17z" />
					<path d="M3.5 13.5V10.5h5.2a1.8 1.8 0 0 1 1.8 1.8v1.2" />
				</svg>
			);
		case "sunday":
			return (
				<svg {...common}>
					<path
						d="M4.5 17.5h15L14.2 8.2 11.4 12l-1.8-2.4L4.5 17.5z"
						fill="currentColor"
						stroke="none"
					/>
				</svg>
			);
		case "dash":
			return (
				<svg {...common}>
					<path d="M7 12h10" />
				</svg>
			);
		default:
			return null;
	}
};

const DayMark = ({ day }) => (
	<div
		className={`my-attendance__day${
			day.is_today ? " my-attendance__day--today" : ""
		}`}>
		<span className="my-attendance__weekday">{day.weekday_label}</span>
		<span className="my-attendance__date">{day.day}</span>
		<span
			className={`my-attendance__mark my-attendance__mark--${day.tone}${
				day.filled ? " is-filled" : " is-outline"
			}`}>
			<StatusIcon name={day.icon} />
		</span>
		<span className="my-attendance__caption">{day.label}</span>
	</div>
);

const MyAttendanceStatus = () => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	const load = useCallback(async () => {
		try {
			const response = await useAxios.get("/attendance/my-status/");
			setData(response.data);
			setError("");
		} catch (err) {
			setError(
				err.response?.data?.error ||
					"근태 현황을 불러오지 못했습니다.",
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
		const timer = window.setInterval(load, 60 * 1000);
		const onVisible = () => {
			if (document.visibilityState === "visible") {
				load();
			}
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [load]);

	const banner = data?.banner;
	const showCard = data?.available && banner;

	return (
		<>
			<PageTitle title="내 근태 현황" />
			<Layout className="member-layout">
				<LeaveNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="my-attendance-page">
							{loading && !data ? (
								<div className="my-attendance my-attendance--loading">
									<Spin />
								</div>
							) : null}
							{!loading && error && !showCard ? (
								<div className="my-attendance my-attendance--empty">
									{error}
								</div>
							) : null}
							{!loading && data && !data.available ? (
								<div className="my-attendance my-attendance--empty">
									{data.message ||
										"근태 대상 정보가 없습니다. 사무국에 문의해 주세요."}
								</div>
							) : null}
							{showCard ? (
								<section className="my-attendance">
									<header className="my-attendance__head">
										<div className="my-attendance__who">
											{data.name}
											{data.department
												? ` · ${data.department}`
												: ""}
										</div>
										<div className="my-attendance__when">
											{data.date_label}
											{data.work_hours_label
												? ` · ${data.work_hours_label}`
												: ""}
										</div>
									</header>

									<div
										className={`my-attendance__banner my-attendance__banner--${banner.tone}`}>
										<div className="my-attendance__badge">
											<StatusIcon
												name={banner.icon}
											/>
										</div>
										<div>
											<h2>{banner.title}</h2>
											<p>{banner.message}</p>
										</div>
									</div>

									<div className="my-attendance__facts">
										<div className="my-attendance__fact">
											<div className="my-attendance__fact-label">
												{data.check_in?.label}
											</div>
											<div
												className={`my-attendance__fact-value my-attendance__fact-value--${data.check_in?.tone || "default"}`}>
												{data.check_in?.value}
											</div>
										</div>
										<div className="my-attendance__fact">
											<div className="my-attendance__fact-label">
												오늘 신청 내역
											</div>
											<div
												className={`my-attendance__fact-value my-attendance__fact-value--${data.application?.tone || "muted"}`}>
												{data.application?.label}
											</div>
										</div>
									</div>

									{data.check_out ? (
										<div className="my-attendance__checkout">
											<div className="my-attendance__fact-label">
												{data.check_out.label}
											</div>
											<div
												className={`my-attendance__fact-value my-attendance__fact-value--${data.check_out.tone || "muted"}`}>
												{data.check_out.value}
											</div>
										</div>
									) : null}

									<section className="my-attendance__week">
										<h3>이번 주 출석</h3>
										<div className="my-attendance__week-row">
											{(data.week || []).map((day) => (
												<DayMark
													key={day.date}
													day={day}
												/>
											))}
										</div>
									</section>

									<footer className="my-attendance__foot">
										<span>
											이번 달 출석{" "}
											{data.month_attended_days}일 · 출근
											미체크 {data.month_unchecked_count}
											건
										</span>
										<span>
											잔여 연차{" "}
											{formatRemainingDays(
												data.remaining_annual_leave,
											)}
										</span>
									</footer>
								</section>
							) : null}
						</div>
					</Content>
				</Layout>
			</Layout>
		</>
	);
};

export default MyAttendanceStatus;
